import React, { useState } from 'react';
import styled from 'styled-components';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { userService } from '../services/userService';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: var(--color-white);
  border: 2px solid var(--color-black);
  padding: 40px;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const ModalTitle = styled.h2`
  font-size: 1.75rem;
  font-weight: bold;
  margin-bottom: 20px;
  color: var(--color-black);
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const InfoText = styled.p`
  font-size: 0.875rem;
  color: var(--color-gray);
  margin-bottom: 20px;
`;

const ErrorMessage = styled.div`
  color: #dc2626;
  font-size: 0.875rem;
  margin-top: 10px;
  padding: 10px;
  background-color: #fee;
  border: 1px solid #dc2626;
`;

const SuccessMessage = styled.div`
  color: #10b981;
  font-size: 0.875rem;
  margin-top: 10px;
  padding: 10px;
  background-color: #efe;
  border: 1px solid #10b981;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
`;

interface ForgotPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = ({ 
  isOpen, 
  onClose,
  onSuccess 
}) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSendResetEmail = async () => {
    setError('');
    setSuccess('');

    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setLoading(true);

    try {
      await userService.forgotPassword(email);
      setSuccess('Password reset email sent! Check your inbox and follow the instructions.');
    } catch (err: any) {
      setError(err.message || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };


  const handleClose = () => {
    setEmail('');
    setError('');
    setSuccess('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={handleClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalTitle>Forgot Password</ModalTitle>

        <InfoText>
          Enter your email address and we'll send you a password reset link.
        </InfoText>

        <FormGroup>
          <Label htmlFor="email">Email Address</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
          />
        </FormGroup>

        <ButtonGroup>
          <Button onClick={handleSendResetEmail} disabled={loading} style={{ flex: 1 }}>
            {loading ? 'Sending...' : 'Send Reset Email'}
          </Button>
          <Button variant="outline" onClick={handleClose} style={{ flex: 1 }}>
            Cancel
          </Button>
        </ButtonGroup>

        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>{success}</SuccessMessage>}
      </ModalContent>
    </ModalOverlay>
  );
};

export default ForgotPasswordModal;

